import { FormEvent, KeyboardEvent, ReactNode, useId, useState } from 'react';

export interface LoginHighlight {
  title: string;
  body: string;
}

interface LoginShellProps {
  product: string;
  headline: ReactNode;
  lede: string;
  highlights: LoginHighlight[];
  footnote?: ReactNode;
  initialEmail?: string;
  initialPassword?: string;
  emailAutoComplete?: 'email' | 'username';
  onLogin: (email: string, password: string) => Promise<void>;
  describeError?: (err: unknown) => string;
}

const KEYFRAMES = `
@keyframes origo-drift { 0%,100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(3%,-4%,0) scale(1.08); } }
@keyframes origo-shake { 0%,100% { transform: translateX(0); } 20%,60% { transform: translateX(-6px); } 40%,80% { transform: translateX(6px); } }
@keyframes origo-rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes origo-ring { 0% { transform: scale(.6); opacity: .55; } 100% { transform: scale(1.9); opacity: 0; } }
@media (prefers-reduced-motion: reduce) { .origo-motion { animation: none !important; } }
`;

function defaultError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return 'Sign-in failed. Try again.';
}

export function OrigoMark({ className = 'h-9 w-9' }: { className?: string }) {
  return (
    <svg viewBox="0 0 40 40" className={className} aria-hidden="true">
      <circle cx="20" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="4.5" className="text-accent" />
      <circle cx="20" cy="20" r="4.5" fill="currentColor" className="text-white" />
    </svg>
  );
}

function Wordmark({ product }: { product: string }) {
  return (
    <div className="flex items-center gap-3">
      <OrigoMark />
      <div className="leading-none">
        <div className="text-[19px] font-semibold tracking-[-0.03em] text-white">origo</div>
        <div className="mt-1 text-[10.5px] font-medium uppercase tracking-[0.18em] text-subtle">{product}</div>
      </div>
    </div>
  );
}

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const MailIcon = () => (
  <svg {...iconProps} className="h-[18px] w-[18px]">
    <rect x="3" y="5" width="18" height="14" rx="3" />
    <path d="m4 7 8 6 8-6" />
  </svg>
);
const LockIcon = () => (
  <svg {...iconProps} className="h-[18px] w-[18px]">
    <rect x="4" y="10.5" width="16" height="10" rx="2.5" />
    <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
  </svg>
);
const EyeIcon = ({ off }: { off: boolean }) => (
  <svg {...iconProps} className="h-[18px] w-[18px]">
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="3" />
    {off && <path d="m4 4 16 16" />}
  </svg>
);
const ArrowIcon = () => (
  <svg {...iconProps} className="h-4 w-4 transition-transform group-hover:translate-x-0.5">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const AlertIcon = () => (
  <svg {...iconProps} className="mt-px h-4 w-4 shrink-0">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5M12 16.2v.3" />
  </svg>
);
const CheckIcon = () => (
  <svg {...iconProps} strokeWidth={2.2} className="h-3.5 w-3.5">
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </svg>
);

function Spinner() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 animate-spin" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity=".25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function BrandPanel({ product, headline, lede, highlights }: Pick<LoginShellProps, 'product' | 'headline' | 'lede' | 'highlights'>) {
  return (
    <aside className="relative hidden overflow-hidden border-r border-white/[0.06] bg-surface lg:flex lg:w-[46%] xl:w-[48%]">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          maskImage: 'radial-gradient(ellipse 80% 70% at 30% 40%, #000 30%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 30% 40%, #000 30%, transparent 75%)',
        }}
      />
      <div
        className="origo-motion pointer-events-none absolute -left-24 -top-32 h-[520px] w-[520px] rounded-full bg-accent/25 blur-[110px]"
        style={{ animation: 'origo-drift 18s ease-in-out infinite' }}
      />
      <div
        className="origo-motion pointer-events-none absolute -bottom-40 right-[-120px] h-[420px] w-[420px] rounded-full bg-accent/10 blur-[100px]"
        style={{ animation: 'origo-drift 24s ease-in-out infinite reverse' }}
      />

      <div className="pointer-events-none absolute right-[12%] top-[18%] hidden xl:block" aria-hidden="true">
        <div className="relative h-40 w-40">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="origo-motion absolute inset-0 rounded-full border border-accent/40"
              style={{ animation: `origo-ring 4.5s ease-out ${i * 1.5}s infinite` }}
            />
          ))}
          <span className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent shadow-[0_0_24px_4px] shadow-accent/60" />
        </div>
      </div>

      <div className="relative z-10 flex w-full flex-col justify-between p-12 xl:p-16">
        <Wordmark product={product} />

        <div className="max-w-[460px]">
          <h2 className="text-[34px] font-semibold leading-[1.1] tracking-[-0.035em] text-white xl:text-[40px]">
            {headline}
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-subtle">{lede}</p>

          <ul className="mt-10 space-y-5">
            {highlights.map((h) => (
              <li key={h.title} className="flex gap-3.5">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent ring-1 ring-accent/30">
                  <CheckIcon />
                </span>
                <div>
                  <div className="text-[14px] font-medium text-white">{h.title}</div>
                  <div className="mt-0.5 text-[13px] leading-relaxed text-subtle">{h.body}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center justify-between text-[12px] text-subtle/80">
          <span>© {new Date().getFullYear()} Origo</span>
          <a href="https://origo.rs" className="transition hover:text-white" target="_blank" rel="noreferrer">
            origo.rs
          </a>
        </div>
      </div>
    </aside>
  );
}

export function LoginShell({
  product,
  headline,
  lede,
  highlights,
  footnote,
  initialEmail = '',
  initialPassword = '',
  emailAutoComplete = 'email',
  onLogin,
  describeError = defaultError,
}: LoginShellProps) {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState(initialPassword);
  const [showPw, setShowPw] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shakeKey, setShakeKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const ids = useId();
  const emailId = `${ids}-email`;
  const pwId = `${ids}-pw`;
  const errId = `${ids}-err`;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await onLogin(email.trim(), password);
    } catch (err) {
      const msg = describeError(err).trim() || 'Sign-in failed. Try again.';
      setError(msg.charAt(0).toUpperCase() + msg.slice(1));
      setShakeKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  const trackCaps = (e: KeyboardEvent<HTMLInputElement>) => setCapsLock(e.getModifierState('CapsLock'));

  const field =
    'peer h-12 w-full rounded-xl border-0 bg-white/[0.035] pl-11 text-[14.5px] text-white placeholder:text-subtle/60 ' +
    'ring-1 ring-inset ring-white/10 transition focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-accent/70 ' +
    'aria-[invalid=true]:ring-bad/60';
  const fieldIcon =
    'pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-subtle transition peer-focus:text-accent';

  return (
    <div className="flex min-h-screen bg-base text-white">
      <style>{KEYFRAMES}</style>
      <BrandPanel product={product} headline={headline} lede={lede} highlights={highlights} />

      <main className="relative flex flex-1 items-center justify-center overflow-hidden px-5 py-12 sm:px-8">
        <div className="pointer-events-none absolute -top-40 left-1/2 h-80 w-[560px] -translate-x-1/2 rounded-full bg-accent/10 blur-[100px] lg:hidden" />

        <div
          className="origo-motion relative w-full max-w-[400px]"
          style={{ animation: 'origo-rise .45s cubic-bezier(.2,.7,.2,1) both' }}
        >
          <div className="mb-10 lg:hidden">
            <Wordmark product={product} />
          </div>

          <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-white">Welcome back</h1>
          <p className="mt-1.5 text-[14px] text-subtle">
            Sign in to <span className="text-white/90">Origo {product}</span> to continue.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <label htmlFor={emailId} className="mb-1.5 block text-[12.5px] font-medium text-white/80">
                Email
              </label>
              <div className="relative">
                <input
                  id={emailId}
                  type="email"
                  inputMode="email"
                  className={field + ' pr-4'}
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(null);
                  }}
                  autoComplete={emailAutoComplete}
                  autoCapitalize="none"
                  spellCheck={false}
                  autoFocus
                  required
                  aria-invalid={!!error}
                  aria-describedby={error ? errId : undefined}
                />
                <span className={fieldIcon}>
                  <MailIcon />
                </span>
              </div>
            </div>

            <div>
              <label htmlFor={pwId} className="mb-1.5 block text-[12.5px] font-medium text-white/80">
                Password
              </label>
              <div className="relative">
                <input
                  id={pwId}
                  type={showPw ? 'text' : 'password'}
                  className={field + ' pr-12'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError(null);
                  }}
                  onKeyDown={trackCaps}
                  onKeyUp={trackCaps}
                  onBlur={() => setCapsLock(false)}
                  autoComplete="current-password"
                  required
                  aria-invalid={!!error}
                  aria-describedby={error ? errId : undefined}
                />
                <span className={fieldIcon}>
                  <LockIcon />
                </span>
                <button
                  type="button"
                  className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-subtle transition hover:bg-white/[0.06] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/70"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  aria-pressed={showPw}
                >
                  <EyeIcon off={showPw} />
                </button>
              </div>
              {capsLock && (
                <p className="mt-2 flex items-center gap-1.5 text-[12px] text-warn">
                  <span className="inline-flex h-4 items-center rounded bg-warn/15 px-1 text-[10px] font-semibold tracking-wide">
                    ⇪
                  </span>
                  Caps Lock is on
                </p>
              )}
            </div>

            {error && (
              <div
                key={shakeKey}
                id={errId}
                role="alert"
                className="origo-motion flex gap-2.5 rounded-xl bg-bad/10 px-3.5 py-3 text-[13px] leading-snug text-[#ffb3bb] ring-1 ring-inset ring-bad/30"
                style={{ animation: 'origo-shake .4s ease-in-out' }}
              >
                <AlertIcon />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="group relative flex h-12 w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-accent text-[14.5px] font-semibold text-[#0b0d12] shadow-[0_8px_30px_-8px] shadow-accent/50 transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-base active:scale-[0.99] disabled:cursor-wait disabled:opacity-80"
            >
              <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/25 to-transparent opacity-60" />
              {busy ? (
                <>
                  <Spinner />
                  <span className="relative">Signing in…</span>
                </>
              ) : (
                <>
                  <span className="relative">Sign in</span>
                  <span className="relative">
                    <ArrowIcon />
                  </span>
                </>
              )}
            </button>
          </form>

          {footnote && <div className="mt-8 text-center text-[12.5px] leading-relaxed text-subtle">{footnote}</div>}

          <div className="mt-10 flex items-center justify-center gap-2 text-[11.5px] text-subtle/70 lg:hidden">
            <span>© {new Date().getFullYear()} Origo</span>
            <span aria-hidden="true">·</span>
            <a href="https://origo.rs" className="hover:text-white">origo.rs</a>
          </div>
        </div>
      </main>
    </div>
  );
}
